/**
 * Phase 18 Ask 4.1 — Command-palette per-collection recents helpers.
 *
 * Persists last-used items per collection (pages / models / logs /
 * presets / scenarios / runs) in localStorage, scoped per-username so
 * a shared workstation can't leak recents across operators.
 */

const COLLECTION_RECENTS_KEY = "uavsimnet_palette_collection_recents_v1";
export const COLLECTION_RECENTS_LIMIT = 5;

function storageKey(base, username) {
    return `${base}:${username || "anon"}`;
}

export function readCollectionRecents(username, collection, limit = COLLECTION_RECENTS_LIMIT) {
    try {
        const raw = localStorage.getItem(storageKey(COLLECTION_RECENTS_KEY, username));
        if (!raw) return [];
        const map = JSON.parse(raw);
        if (!map || typeof map !== "object") return [];
        const bucket = map[collection];
        return Array.isArray(bucket) ? bucket.slice(0, limit) : [];
    } catch {
        return [];
    }
}

export function pushCollectionRecent(username, collection, item) {
    if (!collection || !item || !item.key) return;
    try {
        const key = storageKey(COLLECTION_RECENTS_KEY, username);
        const raw = localStorage.getItem(key);
        const map = raw && typeof raw === "string"
            ? (JSON.parse(raw) || {})
            : {};
        const bucket = Array.isArray(map[collection]) ? map[collection] : [];
        const filtered = bucket.filter((i) => i.key !== item.key);
        map[collection] = [
            { ...item, ts: Date.now() },
            ...filtered,
        ].slice(0, COLLECTION_RECENTS_LIMIT);
        localStorage.setItem(key, JSON.stringify(map));
    } catch {
        /* noop */
    }
}

// Phase 18 close-off Ask B — palette empty-query group builder.
//
// Build the per-collection recent-groups payload the palette renders
// when query is empty. Returns one entry per non-empty bucket, in the
// declared order. Pure function over the reader so it is unit-testable
// without touching the DOM or the palette component.
export const PALETTE_COLLECTION_ORDER = [
    { collection: "scenarios", label: "Recent Scenarios" },
    { collection: "runs",      label: "Recent Runs" },
    { collection: "models",    label: "Recent Models" },
    { collection: "presets",   label: "Recent Presets" },
    { collection: "logs",      label: "Recent Logs" },
    { collection: "pages",     label: "Recent Pages" },
];

export function buildPaletteEmptyQueryGroups(
    username,
    {
        order = PALETTE_COLLECTION_ORDER,
        readBucket = readCollectionRecents,
        limit = 3,
    } = {},
) {
    const groups = [];
    for (const { collection, label } of order) {
        const items = readBucket(username, collection, limit) || [];
        if (items.length > 0) {
            groups.push({ collection, label, items });
        }
    }
    return groups;
}
